import {GetIssuePointCabinetResponsePayload} from '../../../../../shared/models/api/getIssuePointCabinetResponsePayload';
import React from 'react';
import {GenericPointFeature, LngLat} from '@yandex/ymaps3-types';
import {NavigationProp, ParamListBase} from '@react-navigation/native';
import {RegisterClientCabinetResponsePayload} from '../../../../../shared/models/api/registerClientCabinetResponsePayload';
import {EsiaRegCardForm, KidSubform} from '../form/model';
import {AuthorizeEsiaResponsePayload} from '../../../../../shared/models/api/authorizeEsiaResponsePayload';
import {mapFormValuesToEsiaRegData} from '../formMapper';
import {esiaRegisterCardApplication, esiaRegistration} from '../../../../../redux/actions/login';
import {DeliveryType} from '../../../../../types/registerCardApplicationRequestPayload';
import {ParentSaturated} from '../../esiaRegCardState/model';
import {RegisterClientCabinetRequestPayload} from '../../../../../shared/models/api/registerClientCabinetRequestPayload';
import {storage, StorageKeys} from '../../../../../shared/storage';
import {loginSlice} from '../../../../../redux/reducers/login';
import {CardApplicationStatus} from '../../../../../types/enums/cardApplicationStatus';
import {CardStatus} from '../../../../../types/enums/cardStatus';
import {getHttpErrorMessage} from '../../../../../shared/utils/getHttpErrorMessage';

export interface Props {
  onClose: () => void;
}

export interface ViewProps {
  loading?: boolean;
  mapData: GetIssuePointCabinetResponsePayload[];
  onPressMarker: (marker: GetIssuePointCabinetResponsePayload | null) => void;
  currentMarker: GetIssuePointCabinetResponsePayload | null;
  centerPoint: [number, number];
  zoomLevel?: number;
  isIssuePointDetailsVisible: boolean;
  setIsIssuePointDetailsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  markers?: GenericPointFeature<LngLat>[];
}

export const initialYandexZoomValue = 9;
export const initialGoogleZoomValue = 0.8;

export type NativeMapRegion = Partial<{lat: number; lon: number; zoom: number}>;

export interface EsiaRegCardMapProps {
  onGoBack?(): void;
  navigation?: NavigationProp<ParamListBase>;
}

export type ApplicationRegistrationResult = 'success' | 'parentError' | 'parentKidError' | 'kidError' | 'error';

export interface showApplicationRegistrationResultModalParams {
  applicationRegistrationResult: ApplicationRegistrationResult;
  message?: string | undefined;
  _onClose?: () => void;
}

export const submitKidsDataAndSaveErrors = (
  kids: KidSubform[],
  esiaRegCardFormValues: EsiaRegCardForm,
  registrationInfo: AuthorizeEsiaResponsePayload,
  parentId: number | null,
  cardDeliveryInfo: {
    counterpartyDepartmentId: number;
    deliveryTypeId: DeliveryType;
    desiredAddress: string | undefined;
  },
) => {
  const allErrors: string[] = [];
  for (let confirmedKidsIndex = 0; confirmedKidsIndex < kids.length; confirmedKidsIndex++) {
    const kidSubmissionData = mapFormValuesToEsiaRegData(kids[confirmedKidsIndex], registrationInfo.token);
    kidSubmissionData.isEmailNotificationEnabled = esiaRegCardFormValues.isEmailNotificationEnabled;
    kidSubmissionData.isSmsNotificationEnabled = esiaRegCardFormValues.isSmsNotificationEnabled;
    kidSubmissionData.agentInfo = {
      document: kidSubmissionData.number,
      firstName: esiaRegCardFormValues.firstName,
      lastName: esiaRegCardFormValues.lastName,
      middleName: esiaRegCardFormValues.middleName,
      passport: `Паспорт ${esiaRegCardFormValues.number}, ${esiaRegCardFormValues.codeOrg}, ${esiaRegCardFormValues.date}, ${esiaRegCardFormValues.org}`,
      parentAccountId: parentId,
    };
    kidSubmissionData.linkParentAccount = esiaRegCardFormValues.isAccountLinkingEnabled;
    const registerKid = async () => {
      if (registrationInfo.token == null) return;
      return await esiaRegisterCardApplication(
        {
          ...kidSubmissionData,
          ...cardDeliveryInfo,
          accessToken: registrationInfo.token,
        },
        false,
      ).catch((e) => {
        const errorAssembled = `Ошибка регистрации ${kidSubmissionData.firstName} ${
          kidSubmissionData.lastName
        }. Код: ${getHttpErrorMessage(e, 'неизвестная ошибка')}`;
        allErrors.push(errorAssembled);
      });
    };
    registerKid();
  }
  return allErrors.join('. \n\n');
};

export const registerParentStoreActivationTokenReturnID = async (
  _parent: ParentSaturated | undefined,
  submissionData: Omit<
    RegisterClientCabinetRequestPayload,
    'counterpartyDepartmentId' | 'deliveryTypeId' | 'desiredAddress'
  >,
  cardDeliveryInfo: {
    counterpartyDepartmentId: number;
    deliveryTypeId: DeliveryType;
    desiredAddress: string | undefined;
  },
): Promise<{registered: boolean; counterpartyId: number | null}> => {
  if (!_parent?.checked) return {registered: false, counterpartyId: null};

  let parentRegistrationResponse: RegisterClientCabinetResponsePayload | undefined;

  const cabinetToken = await storage.getItem<string>(StorageKeys.token);
  const isNewClientWithoutCard =
    !cabinetToken && _parent.cardApplicationStatusId == null && _parent.cardStatusId == null;

  if (isNewClientWithoutCard) {
    parentRegistrationResponse = await esiaRegistration({...submissionData, ...cardDeliveryInfo});

    if (!parentRegistrationResponse?.counterpartyId) {
      throw new Error('Ошибка регистрации родителя');
    }
    await storage.setItem(StorageKeys.activationToken, parentRegistrationResponse.activationToken);
  }

  await esiaRegisterCardApplication({
    ...submissionData,
    ...cardDeliveryInfo,
  });

  return {registered: true, counterpartyId: parentRegistrationResponse?.counterpartyId ?? null};
};

export const saveCreatedApplicationSnapshot = async (
  dispatch: (action: unknown) => unknown,
  regInfo: AuthorizeEsiaResponsePayload,
  cardDeliveryInfo: unknown,
  parent: {cardStatusId?: number | null} | null | undefined,
  isLogged: boolean,
): Promise<{redirectToActivation: boolean}> => {
  const createdRegInfo: AuthorizeEsiaResponsePayload = {
    ...regInfo,
    cardDeliveryInfo: cardDeliveryInfo as AuthorizeEsiaResponsePayload['cardDeliveryInfo'],
    cardApplicationStatusId: CardApplicationStatus.AcceptFromClient,
    cardStatusId: regInfo.token ? regInfo.cardStatusId : undefined,
  };
  const redirectToActivation = !isLogged || parent?.cardStatusId === CardStatus.Blocked;

  await dispatch(loginSlice.actions.setEsiaRegistrationInfo(createdRegInfo));
  await storage.setItem(StorageKeys.esiaRegInfo, createdRegInfo);

  return {redirectToActivation};
};

export const setCardDeliveryInfo = (
  selectedDeliveryType: DeliveryType,
  currentMarker: GetIssuePointCabinetResponsePayload | null,
  russianPostDeliveryAddress: string,
) => {
  return {
    counterpartyDepartmentId: selectedDeliveryType === DeliveryType.IssuePoint ? currentMarker!.id : 531,
    deliveryTypeId: selectedDeliveryType,
    desiredAddress:
      selectedDeliveryType === DeliveryType.IssuePoint ? currentMarker?.address : russianPostDeliveryAddress,
  };
};
